import React, {ErrorInfo, ReactNode} from 'react'

import ErrorReportModal from './ErrorReportModal.js'

interface ErrorBoundaryProps {
    children?: ReactNode
}

interface ErrorBoundaryState {
    err?: Error
}

/**
 * Catches a render error in its children, logs it to the server and shows it in a modal (ErrorReportModal) instead
 * of the children, with a button to report it. Closing the modal renders the children again. A class, because React
 * has no hook for catching render errors.
 */
export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = {}

    static getDerivedStateFromError(err: Error) {
        return {err}
    }

    componentDidCatch(err: Error, info: ErrorInfo) {
        fetch('/client-side-error', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionStorage.token || localStorage.mtoken}`
            },
            body: JSON.stringify({error: {
                name: err.name || 'Client Side Error',
                message: err.message || 'An error occurred',
                stack: err.stack,
                componentStack: info.componentStack,
                url: window.location.toString()
            }})
        })
    }

    reset = () => {
        this.setState({err: undefined})
    }

    render() {
        const {err} = this.state

        if (!err) {
            return this.props.children
        }

        return <ErrorReportModal show err={err} onClose={this.reset} />
    }
}
